/**
 * Admin Paysera credentials editor (Iter 67s).
 *
 * Single-card form: paste Project ID + Client ID + Client Secret, toggle
 * Sandbox/Production, hit Save. The backend stores the row in
 * `platform_paysera_config` and busts the in-memory token cache.
 *
 * The component never re-reads the secret back from the server — we only
 * show whether one is set. To rotate, the admin pastes a new value.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CreditCard, KeyRound, BadgeCheck, FlaskConical, Trash2 } from "lucide-react";

export default function AdminPayseraConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [sandbox, setSandbox] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/paysera/config");
      setCfg(data);
      setProjectId(data.project_id || "");
      setClientId(data.client_id || "");
      setSandbox(data.sandbox !== false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!projectId.trim() || !clientId.trim() || !clientSecret.trim()) {
      toast.error("Project ID, Client ID, and Client Secret are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/paysera/config", {
        project_id: projectId.trim(),
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
        sandbox,
      });
      toast.success("Paysera credentials saved.");
      setClientSecret("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!confirm("Disconnect Paysera? Vendors won't be able to pay via Paysera until you save new credentials.")) return;
    try {
      await api.delete("/admin/paysera/config");
      toast.success("Paysera disconnected.");
      setClientSecret("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="rounded-3xl border border-violet-100 bg-gradient-to-br from-sky-50/40 via-white to-violet-50/40 p-6" data-testid="admin-paysera-config">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-11 w-11 rounded-2xl bg-violet-50 ring-1 ring-violet-100 flex items-center justify-center shrink-0">
          <CreditCard className="h-5 w-5 text-violet-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-semibold leading-tight">Paysera payment gateway</h3>
          <p className="text-sm text-neutral-600 mt-0.5">
            Lets vendors pay subscription &amp; commission balances directly by card. Funds settle into your single Paysera merchant project.
          </p>
        </div>
        {cfg?.configured && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
            <BadgeCheck className="h-3 w-3" /> Configured
          </span>
        )}
      </div>

      <form onSubmit={save} className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500 flex items-center gap-1.5">
              <KeyRound className="h-3 w-3 text-violet-600" /> Project ID
            </Label>
            <Input
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              placeholder="01990618-2c90-..."
              className="mt-1 rounded-xl"
              data-testid="paysera-project-id"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500 flex items-center gap-1.5">
              <KeyRound className="h-3 w-3 text-indigo-600" /> Client ID
            </Label>
            <Input
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="01990618-2c90-..."
              className="mt-1 rounded-xl"
              data-testid="paysera-client-id"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-neutral-500 flex items-center gap-1.5">
            <KeyRound className="h-3 w-3 text-rose-600" /> Client Secret
          </Label>
          <Input
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder={cfg?.has_secret ? "•••••••• (saved — paste a new secret to rotate)" : "Your Paysera client secret"}
            className="mt-1 rounded-xl"
            data-testid="paysera-client-secret"
          />
          <div className="text-[11px] text-neutral-500 mt-1">
            Never shown back. Get this from <span className="font-semibold">bank.paysera.com → your Checkout project → Integrations</span>.
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={sandbox}
            onChange={(e) => setSandbox(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-indigo-600 focus:ring-indigo-500"
            data-testid="paysera-sandbox-toggle"
          />
          <FlaskConical className="h-3.5 w-3.5 text-amber-600" />
          Sandbox mode (test API endpoints, no real money)
        </label>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            type="submit"
            disabled={saving}
            className="rounded-full bg-gradient-to-r from-sky-600 via-indigo-600 to-violet-600 hover:from-sky-700 hover:via-indigo-700 hover:to-violet-700 text-white"
            data-testid="paysera-save-btn"
          >
            {saving ? "Saving…" : cfg?.configured ? "Update credentials" : "Save credentials"}
          </Button>
          {cfg?.configured && (
            <Button
              type="button"
              variant="ghost"
              onClick={clear}
              className="rounded-full text-rose-600 hover:bg-rose-50"
              data-testid="paysera-clear-btn"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Disconnect
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
